import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Clock, ChefHat, ArrowRight, Users, Flame } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { recipesData, getFeaturedRecipe } from './recipesData'

export function FeaturedRecipes() {
  const { t } = useTranslation('common')
  const featuredRecipe = getFeaturedRecipe()
  const gridRecipes = recipesData.filter((r) => !r.featured)

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'medium':
        return 'bg-amber-100 text-amber-700 border-amber-200'
      case 'hard':
        return 'bg-red-100 text-red-700 border-red-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return <ChefHat className="w-3 h-3" />
      case 'medium':
        return <Flame className="w-3 h-3" />
      case 'hard':
        return <Flame className="w-3 h-3" />
      default:
        return <ChefHat className="w-3 h-3" />
    }
  }

  return (
    <section className="section-padding bg-white" aria-labelledby="featured-title">
      <div className="container-fiorucci">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#DF631E]/10 text-[#DF631E] text-sm font-semibold rounded-full mb-4">
            {t('recipes.featured.title')}
          </span>
          <h2 id="featured-title" className="heading-section text-[#4E2118] mb-4">
            {t('recipes.featured.subtitle')}
          </h2>
          <p className="text-body-lg text-[#4E2118]/70 max-w-2xl mx-auto">
            {t('recipes.hero.description')}
          </p>
        </motion.div>

        {/* Featured Recipe - Large Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <LocalizedLink to={`recipes/${featuredRecipe.id}`} className="group block">
            <article className="relative bg-[#FDF8F3] rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Image Side */}
                <div className="relative h-64 lg:h-auto lg:min-h-[450px] overflow-hidden">
                  <img
                    src={featuredRecipe.image}
                    alt={t(`recipes.recipes.${featuredRecipe.translationKey}.title`)}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#FDF8F3]/20 lg:bg-gradient-to-l lg:from-[#FDF8F3] lg:via-[#FDF8F3]/50 lg:to-transparent" />

                  {/* Featured Badge */}
                  <div className="absolute top-6 left-6 px-4 py-2 bg-[#DF631E] text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-2">
                    <Flame className="w-4 h-4" />
                    {t('recipes.featured.title')}
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-6 right-6 px-3 py-1.5 bg-white/95 backdrop-blur-sm text-[#4E2118] text-xs font-bold rounded-full shadow-lg">
                    {featuredRecipe.category}
                  </div>
                </div>

                {/* Content Side */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <h3 className="font-serif text-3xl lg:text-4xl font-bold text-[#4E2118] mb-4 group-hover:text-[#DF631E] transition-colors">
                    {t(`recipes.recipes.${featuredRecipe.translationKey}.title`)}
                  </h3>

                  <p className="text-[#4E2118]/70 text-lg leading-relaxed mb-8">
                    {t(`recipes.recipes.${featuredRecipe.translationKey}.description`)}
                  </p>

                  {/* Meta Info */}
                  <div className="flex flex-wrap gap-4 mb-8">
                    <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                      <Clock className="w-4 h-4 text-[#DF631E]" />
                      <span className="text-sm font-medium text-[#4E2118]">
                        {t(`recipes.recipes.${featuredRecipe.translationKey}.time`)}
                      </span>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${getDifficultyColor(t(`recipes.recipes.${featuredRecipe.translationKey}.difficulty`))}`}>
                      {getDifficultyIcon(t(`recipes.recipes.${featuredRecipe.translationKey}.difficulty`))}
                      <span className="text-sm font-medium">
                        {t(`recipes.featured.${t(`recipes.recipes.${featuredRecipe.translationKey}.difficulty`)}`)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                      <Users className="w-4 h-4 text-[#5C1A1A]" />
                      <span className="text-sm font-medium text-[#4E2118]">
                        {featuredRecipe.servings} {t('recipes.featured.servings')}
                      </span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="flex items-center gap-4">
                    <span className="btn-primary inline-flex items-center gap-2 group">
                      {t('recipes.featured.viewRecipe')}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </div>
            </article>
          </LocalizedLink>
        </motion.div>

        {/* Recipe Grid - Smaller Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gridRecipes.map((recipe, index) => (
            <motion.div
              key={recipe.id}
              initial={{ opacity: 1, y: 0 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <LocalizedLink to={`recipes/${recipe.id}`} className="group block">
                <article className="relative bg-[#FDF8F3] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                  {/* Image Container */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={recipe.image}
                      alt={t(`recipes.recipes.${recipe.translationKey}.title`)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/70 via-transparent to-transparent" />

                    {/* Category Badge */}
                    <div className="absolute top-4 left-4 px-3 py-1 bg-white/95 backdrop-blur-sm text-[#4E2118] text-xs font-bold rounded-full">
                      {recipe.category}
                    </div>

                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="font-serif text-xl font-bold text-white mb-2 group-hover:text-[#F3E4D6] transition-colors">
                        {t(`recipes.recipes.${recipe.translationKey}.title`)}
                      </h3>
                    </div>
                  </div>

                  {/* Bottom Section */}
                  <div className="p-5 bg-white flex-1 flex flex-col">
                    <p className="text-[#4E2118]/70 text-sm leading-relaxed mb-4 flex-1">
                      {t(`recipes.recipes.${recipe.translationKey}.description`)}
                    </p>

                    {/* Meta info */}
                    <div className="flex items-center justify-between pt-4 border-t border-[#F3E4D6]">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 text-[#4E2118]/70 text-sm">
                          <Clock className="w-4 h-4 text-[#DF631E]" />
                          <span>{t(`recipes.recipes.${recipe.translationKey}.time`)} </span>
                        </div>
                        <div className={`flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${getDifficultyColor(t(`recipes.recipes.${recipe.translationKey}.difficulty`))}`}>
                          {getDifficultyIcon(t(`recipes.recipes.${recipe.translationKey}.difficulty`))}
                          <span>{t(`recipes.featured.${t(`recipes.recipes.${recipe.translationKey}.difficulty`)}`)} </span>
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-[#FDF8F3] flex items-center justify-center group-hover:bg-[#DF631E] transition-colors">
                        <ArrowRight className="w-4 h-4 text-[#4E2118] group-hover:text-white transition-colors" />
                      </div>
                    </div>
                  </div>
                </article>
              </LocalizedLink>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <LocalizedLink
            to="recipes"
            className="btn-secondary inline-flex items-center gap-2 group"
          >
            {t('home.recipes.cta')}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </LocalizedLink>
        </motion.div>
      </div>
    </section>
  )
}
